/**
 * Auto-plan wizard (D33, `/plan/week/auto`): the wizard's answers, their
 * localStorage round-trip, turning them into a planner v2 request, and the
 * small summaries the proposal screen shows. Pure — no React, no fetches.
 *
 * Answers are keyed by weekday (0 = Sunday) rather than date so next week's
 * wizard starts from this week's choices.
 */
import { z } from 'zod';
import {
  cuisineSchema,
  mealSlotSchema,
  plannerVibeSchema,
  type Cuisine,
  type MealSlot,
  type MealTimeDto,
  type PlannerCostDto,
  type PlannerDayDto,
  type PlannerVibe,
  type PlannerWeekRequestInput,
} from '@fewerbags/schema';
import { addDays, isIsoDate, parseIsoDate } from './planDates';
import { acceptableMeals, type PlannerLock } from './planner';
import { SLOT_ORDER } from './plans';

export const AUTOPLAN_DAYS = 7;

/** Who eats a meal. `personIds: null` = the usual people (meal time, else everyone). */
export interface AttendeeChoice {
  personIds: string[] | null;
  guests: string[];
}

export interface DineOutChoice {
  note: string;
  /** null = the API default ($15 per person). */
  estCostCents: number | null;
}

export interface AutoplanState {
  from: string;
  /** 0 = Sunday … 6 = Saturday. */
  weekdays: number[];
  slots: MealSlot[];
  /** Per-slot default for every day. */
  slotAttendees: Partial<Record<MealSlot, AttendeeChoice>>;
  /** Per weekday×slot override (`cellKey`). */
  cellAttendees: Record<string, AttendeeChoice>;
  /** Dining out on a weekday×slot (`cellKey`). */
  dineOut: Record<string, DineOutChoice>;
  /** Weekday (as a string) → meal place id where lunch is packed for. Missing = home. */
  packed: Record<string, string>;
  vibe: PlannerVibe;
  cuisines: Cuisine[];
  budgetCents: number | null;
  includeSides: boolean;
}

/** The per-meal choices made on the proposal screen (step 3). */
export interface AutoplanStaging {
  exclude: Record<string, string[]>;
  locked: Record<string, PlannerLock>;
  skip: string[];
}

export const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export function defaultAutoplanState(from: string): AutoplanState {
  return {
    from,
    weekdays: [...ALL_WEEKDAYS],
    slots: ['dinner'],
    slotAttendees: {},
    cellAttendees: {},
    dineOut: {},
    packed: {},
    vibe: 'any',
    cuisines: [],
    budgetCents: null,
    includeSides: false,
  };
}

export function cellKey(weekday: number, slot: MealSlot): string {
  return `${weekday}|${slot}`;
}

export function weekdayOf(iso: string): number {
  return parseIsoDate(iso).getDay();
}

/** The range's dates whose weekday is selected, in order. */
export function selectedDates(state: Pick<AutoplanState, 'from' | 'weekdays'>): string[] {
  const out: string[] = [];
  for (let i = 0; i < AUTOPLAN_DAYS; i++) {
    const date = addDays(state.from, i);
    if (state.weekdays.includes(weekdayOf(date))) out.push(date);
  }
  return out;
}

export function orderedSlots(slots: readonly MealSlot[]): MealSlot[] {
  return SLOT_ORDER.filter((s) => slots.includes(s));
}

export const USUAL_ATTENDEES: AttendeeChoice = { personIds: null, guests: [] };

/** The cell override, else the slot's default, else the usual people. */
export function attendeeChoiceFor(
  state: Pick<AutoplanState, 'slotAttendees' | 'cellAttendees'>,
  weekday: number,
  slot: MealSlot,
): AttendeeChoice {
  return (
    state.cellAttendees[cellKey(weekday, slot)] ?? state.slotAttendees[slot] ?? USUAL_ATTENDEES
  );
}

export function isUsual(choice: AttendeeChoice): boolean {
  return choice.personIds === null && choice.guests.length === 0;
}

/** Guest names for a proposed meal (the proposal only counts them). */
export function guestNamesFor(state: AutoplanState, day: Pick<PlannerDayDto, 'date' | 'slot'>) {
  return attendeeChoiceFor(state, weekdayOf(day.date), day.slot).guests;
}

/**
 * Wizard answers + proposal-screen choices → `POST /planner/week` body.
 * `personIds` is the household's people: a choice of "everyone plus a guest"
 * has to name everyone, since an empty list means "the usual people".
 * `placeIds` drops packed places that no longer exist.
 */
export function buildWeekRequest(
  state: AutoplanState,
  staging: AutoplanStaging,
  personIds: readonly string[],
  placeIds?: ReadonlySet<string>,
): PlannerWeekRequestInput {
  const slots = orderedSlots(state.slots);
  const dates = selectedDates(state);
  const known = new Set(personIds);
  const attendees: NonNullable<PlannerWeekRequestInput['attendees']> = {};
  const dineOut: NonNullable<PlannerWeekRequestInput['dineOut']> = [];
  const packed: NonNullable<PlannerWeekRequestInput['packed']> = {};

  for (const date of dates) {
    const wd = weekdayOf(date);
    for (const slot of slots) {
      const choice = attendeeChoiceFor(state, wd, slot);
      if (!isUsual(choice)) {
        const ids =
          choice.personIds === null
            ? [...personIds]
            : choice.personIds.filter((id) => known.has(id));
        (attendees[date] ??= {})[slot] = { personIds: ids, guests: choice.guests.length };
      }
      const out = state.dineOut[cellKey(wd, slot)];
      if (out) {
        const note = out.note.trim();
        dineOut.push({
          date,
          slot,
          ...(note ? { note: note.slice(0, 200) } : {}),
          ...(out.estCostCents !== null ? { estCostCents: out.estCostCents } : {}),
        });
        continue;
      }
      const placeId = slot === 'lunch' ? state.packed[String(wd)] : undefined;
      if (placeId && (!placeIds || placeIds.has(placeId))) {
        (packed[date] ??= {})[slot] = placeId;
      }
    }
  }

  return {
    from: state.from,
    days: AUTOPLAN_DAYS,
    slot: slots.includes('dinner') ? 'dinner' : (slots[0] ?? 'dinner'),
    slots: slots.length ? slots : ['dinner'],
    ...(state.weekdays.length < 7 ? { weekdays: [...state.weekdays].sort() } : {}),
    includeSides: state.includeSides,
    exclude: staging.exclude,
    locked: staging.locked,
    skip: staging.skip,
    ...(Object.keys(attendees).length ? { attendees } : {}),
    ...(state.vibe !== 'any' ? { vibe: state.vibe } : {}),
    ...(state.cuisines.length ? { cuisines: state.cuisines } : {}),
    ...(state.budgetCents !== null ? { budgetCents: state.budgetCents } : {}),
    ...(dineOut.length ? { dineOut } : {}),
    ...(Object.keys(packed).length ? { packed } : {}),
  };
}

// ------------------------------------------------------------ storage --

export function autoplanStorageKey(householdId: string): string {
  return `fewerbags:autoplan:${householdId}`;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

const id = z.string().min(1).max(64);
const attendeeChoiceSchema = z.object({
  personIds: z.array(id).max(50).nullable(),
  guests: z.array(z.string().trim().min(1).max(80)).max(30),
});
const cellKeySchema = z.string().regex(/^[0-6]\|(breakfast|lunch|dinner|snack)$/);
const fields = {
  from: z.string().refine((v) => isIsoDate(v)),
  weekdays: z.array(z.number().int().min(0).max(6)).max(7),
  slots: z.array(mealSlotSchema).min(1).max(4),
  slotAttendees: z.partialRecord(mealSlotSchema, attendeeChoiceSchema),
  cellAttendees: z.record(cellKeySchema, attendeeChoiceSchema),
  dineOut: z.record(
    cellKeySchema,
    z.object({
      note: z.string().max(200),
      estCostCents: z.number().int().min(0).max(10_000_000).nullable(),
    }),
  ),
  packed: z.record(z.string().regex(/^[0-6]$/), id),
  vibe: plannerVibeSchema,
  cuisines: z.array(cuisineSchema).max(30),
  budgetCents: z.number().int().min(0).max(10_000_000).nullable(),
  includeSides: z.boolean(),
} satisfies { [K in keyof AutoplanState]: z.ZodType };

/**
 * Saved answers → state. Each field is checked on its own, so one bad field
 * (an old format, hand-edited storage) falls back to its default without
 * losing the rest. A saved week that has already started is replaced by
 * `defaultFrom`.
 */
export function parseAutoplanState(raw: unknown, today: string, defaultFrom: string) {
  const state = defaultAutoplanState(defaultFrom);
  if (!raw || typeof raw !== 'object') return state;
  const obj = raw as Record<string, unknown>;
  const out = state as unknown as Record<string, unknown>;
  for (const [key, schema] of Object.entries(fields) as [keyof AutoplanState, z.ZodType][]) {
    const parsed = schema.safeParse(obj[key]);
    if (parsed.success) out[key] = parsed.data;
  }
  state.weekdays = [...new Set(state.weekdays)].sort();
  state.slots = orderedSlots(state.slots);
  if (state.from < today) state.from = defaultFrom;
  return state;
}

export function loadAutoplanState(
  householdId: string,
  today: string,
  defaultFrom: string,
  storage: StorageLike | null = defaultStorage(),
): AutoplanState {
  try {
    const raw = storage?.getItem(autoplanStorageKey(householdId));
    return parseAutoplanState(raw ? JSON.parse(raw) : null, today, defaultFrom);
  } catch {
    return defaultAutoplanState(defaultFrom);
  }
}

export function saveAutoplanState(
  householdId: string,
  state: AutoplanState,
  storage: StorageLike | null = defaultStorage(),
): void {
  try {
    storage?.setItem(autoplanStorageKey(householdId), JSON.stringify(state));
  } catch {
    // Best-effort: a full or blocked storage never breaks planning.
  }
}

// ------------------------------------------------------- proposal view --

export interface MealsOnDate {
  date: string;
  meals: PlannerDayDto[];
}

/** Proposal meals grouped by date, slots in meal order. */
export function groupMealsByDate(days: readonly PlannerDayDto[]): MealsOnDate[] {
  const byDate = new Map<string, PlannerDayDto[]>();
  for (const d of days) {
    const list = byDate.get(d.date) ?? [];
    list.push(d);
    byDate.set(d.date, list);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, meals]) => ({
      date,
      meals: [...meals].sort((a, b) => SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot)),
    }));
}

/** "$23" (whole dollars, for estimates). */
export function formatDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

/** "≈ $23 · 2 prices missing"; null without an estimate. */
export function costLine(cost: PlannerCostDto | null | undefined): string | null {
  if (!cost) return null;
  const n = cost.missing.length;
  return `≈ ${formatDollars(cost.totalCents)}${n ? ` · ${n} ${n === 1 ? 'price' : 'prices'} missing` : ''}`;
}

/** Warnings across a meal's picks (main and sides). */
export function mealWarnings(day: PlannerDayDto) {
  return [...(day.main?.warnings ?? []), ...day.sides.flatMap((s) => s.warnings)];
}

export interface WeekSummary {
  meals: number;
  totalCents: number;
  budgetCents: number | null;
  overBudget: boolean;
  warnings: number;
  missingPrices: number;
}

/**
 * Totals over the meals that would be added (planned and skipped meals don't
 * count). The budget comes from the response, else the request.
 */
export function weekSummary(
  days: readonly PlannerDayDto[],
  budgetCents: number | null,
): WeekSummary {
  const meals = acceptableMeals(days);
  const missing = new Set<string>();
  let total = 0;
  let warnings = 0;
  for (const m of meals) {
    total += m.cost?.totalCents ?? 0;
    for (const id of m.cost?.missing ?? []) missing.add(id);
    warnings += mealWarnings(m).length;
  }
  return {
    meals: meals.length,
    totalCents: total,
    budgetCents,
    overBudget: budgetCents !== null && total > budgetCents,
    warnings,
    missingPrices: missing.size,
  };
}

/** "45", "45.5", "$45.50" → cents; empty or invalid → null. */
export function parseDollars(input: string): number | null {
  const m = /^\s*\$?\s*(\d{1,6})(?:\.(\d{0,2}))?\s*$/.exec(input);
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
}

/** Cents → the input's text ("45", "45.50"). */
export function dollarsInput(cents: number | null): string {
  if (cents === null) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/** The household's usual serve time for a weekday×slot, if it has one. */
export function usualServeAt(
  mealTimes: readonly MealTimeDto[],
  weekday: number,
  slot: MealSlot,
): string | null {
  return mealTimes.find((m) => m.weekday === weekday && m.slot === slot)?.serveAt ?? null;
}

/** The most common usual serve time for a slot across the week (column hint). */
export function typicalServeAt(mealTimes: readonly MealTimeDto[], slot: MealSlot): string | null {
  const counts = new Map<string, number>();
  for (const m of mealTimes) {
    if (m.slot === slot) counts.set(m.serveAt, (counts.get(m.serveAt) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [t, n] of counts) {
    if (n > bestN || (n === bestN && best !== null && t < best)) {
      best = t;
      bestN = n;
    }
  }
  return best;
}

/** Short label for who eats a meal: "Everyone", "Usual", "Ann, Ben +1". */
export function attendeeSummary(
  choice: AttendeeChoice,
  names: ReadonlyMap<string, string>,
  usualLabel = 'Usual',
): string {
  const guests = choice.guests.length ? ` +${choice.guests.length}` : '';
  if (choice.personIds === null)
    return `${choice.guests.length ? 'Everyone' : usualLabel}${guests}`;
  const listed = choice.personIds.map((pid) => names.get(pid)).filter(Boolean) as string[];
  if (listed.length === 0) return choice.guests.length ? `Guests${guests}` : 'Nobody';
  const shown =
    listed.length > 2
      ? `${listed.slice(0, 2).join(', ')} +${listed.length - 2}`
      : listed.join(', ');
  return `${shown}${guests}`;
}
